import moment from 'moment/moment'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import { EuiDatePicker, EuiFieldNumber, EuiFormRow, EuiSelect, EuiSpacer, EuiText } from '@elastic/eui'

import { Campaign, CampaignActiveStatus, CampaignStatus, CampaignType } from 'api/interfaces'
import { IntervalUnit } from 'api/interfaces/Bundle'
import { RootState } from 'app/rootReducer'
import { AcEuiFormRow } from 'components/Forms/AcEuiFormRow'
import { useWhiteLabel } from 'whiteLabel/WhiteLabelContext'

export interface CampaignBudget {
  budget: number
  start?: moment.Moment
  end?: moment.Moment
  method: BudgetMethod
  totalIntervals?: number
  intervalUnit?: IntervalUnit
}

export interface IAgencyV2CampaignBudgetSetter {
  campaign: Campaign
  onBudgetChange?: (value: CampaignBudget) => void
  campaignBudget: CampaignBudget
}

export enum BudgetMethod {
  Indefinite = 'indefinite',
  Intervals = 'intervals',
  Dates = 'dates'
}

export const InternetMinWeeklyBudget: number = 70
export const TvMinWeeklyBudget: number = 200
export const BbMinWeeklyBudget: number = 150

export const getMinBudgetForMethod = (minWeeklyBudget: number, campaignBudget: CampaignBudget) => {
  if (campaignBudget.method === BudgetMethod.Dates && campaignBudget.start && campaignBudget.end) {
    const start = moment(campaignBudget.start).startOf('day')
    const end = moment(campaignBudget.end).endOf('day')

    const days = Math.abs(end.diff(start, 'days')) + 1
    return Number(((minWeeklyBudget / 7) * days).toFixed(2))
  }

  if (campaignBudget.intervalUnit === IntervalUnit.Week) {
    return minWeeklyBudget
  }

  if (campaignBudget.intervalUnit === IntervalUnit.Month) {
    return minWeeklyBudget * 4
  }

  return minWeeklyBudget
}

export const AgencyV2CampaignBudgetSetter: React.FC<IAgencyV2CampaignBudgetSetter> = props => {
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  })

  const whiteLabel = useWhiteLabel()
  const { isShopifyUser, isStaffUser } = useSelector((state: RootState) => state.app)

  const [minWeeklyBudget, setMinWeeklyBudget] = useState(InternetMinWeeklyBudget)
  const [minStartDate, setMinStartDate] = useState(moment())
  const [budgetInputValue, setBudgetInputValue] = useState<string>('')

  const minBudget = useMemo(() => getMinBudgetForMethod(minWeeklyBudget, props.campaignBudget), [minWeeklyBudget, props.campaignBudget])

  const canBudgetBeEmpty = useMemo(() => {
    if (!props.campaign) {
      return false
    }

    return !props.campaign.hasEditedBudget && (props.campaign.status === CampaignStatus.SavedForLater || props.campaign.status === CampaignStatus.Disapproved)
  }, [props.campaign])

  const minBudgetForField = useMemo(() => {
    if (!canBudgetBeEmpty && props.campaign.budget > 0 && props.campaign.budget < minBudget) {
      return props.campaign.budget
    }
    return minBudget
  }, [canBudgetBeEmpty, props.campaign.budget, minBudget])

  useEffect(() => {
    if (props.campaign.type === CampaignType.Internet) {
      setMinWeeklyBudget(InternetMinWeeklyBudget)
    } else if (props.campaign.type === CampaignType.TV) {
      setMinWeeklyBudget(TvMinWeeklyBudget)
    } else if (props.campaign.type === CampaignType.Billboard) {
      setMinWeeklyBudget(BbMinWeeklyBudget)
    } else if (props.campaign.type === CampaignType.Audio) {
      setMinWeeklyBudget(TvMinWeeklyBudget)
    }

    if (isStaffUser) {
      setMinStartDate(moment())
    } else if (props.campaign?.activated) {
      setMinStartDate(moment(props.campaign?.activated).add(3, 'day'))
    } else {
      setMinStartDate(moment().add(3, 'day'))
    }
  }, [props.campaign, whiteLabel, isStaffUser])

  useEffect(() => {
    if (canBudgetBeEmpty && props.campaignBudget.budget <= 0) {
      setBudgetInputValue('')
    } else {
      setBudgetInputValue(props.campaignBudget.budget.toString())
    }
  }, [props.campaignBudget.budget, canBudgetBeEmpty])

  const getBudgetLabel = useCallback(() => {
    if (!props.campaign) {
      return ''
    }

    if (isShopifyUser) {
      return `30 Day Budget`
    }

    if (props.campaign?.hasBundle) {
      return 'Campaign Budget'
    }

    if (props.campaignBudget) {
      switch (props.campaignBudget.method) {
        case BudgetMethod.Indefinite:
          if (props.campaignBudget.intervalUnit === IntervalUnit.Month) {
            return 'Monthly Budget'
          }
          return 'Weekly Budget'
        case BudgetMethod.Dates:
          return 'Total Budget'
        case BudgetMethod.Intervals:
          if (props.campaignBudget.intervalUnit === IntervalUnit.Month) {
            return 'Monthly Budget'
          }
          return 'Weekly Budget'
        default:
          return `${props.campaign?.budgetFrequency} Budget`
      }
    }

    return `${props.campaign?.budgetFrequency} Budget`
  }, [props.campaign, props.campaignBudget, isShopifyUser, minWeeklyBudget])

  const onBudgetChange = (inputValue: string) => {
    setBudgetInputValue(inputValue)
    if (props.onBudgetChange) {
      const numericValue = inputValue === '' ? 0 : Number(inputValue)
      const newBudget = {
        ...props.campaignBudget,
        budget: numericValue
      }
      props.onBudgetChange(newBudget)
    }
  }

  const onStartChange = (value: moment.Moment) => {
    if (props.onBudgetChange) {
      const momentValue = moment.utc(value.format('MM/DD/yyyy'))
      const newBudget = {
        ...props.campaignBudget,
        start: momentValue
      }
      props.onBudgetChange(newBudget)
    }
  }

  const onEndChange = (value: moment.Moment) => {
    if (props.onBudgetChange) {
      const momentValue = moment.utc(value.format('MM/DD/yyyy'))
      const newBudget = {
        ...props.campaignBudget,
        end: momentValue
      }
      props.onBudgetChange(newBudget)
    }
  }

  const onDurationChange = (value: number) => {
    if (props.onBudgetChange) {
      const newBudget: CampaignBudget = {
        ...props.campaignBudget,
        totalIntervals: value
      }
      props.onBudgetChange(newBudget)
    }
  }

  const onIntervalUnitChange = (value: IntervalUnit) => {
    if (props.onBudgetChange) {
      const newBudget: CampaignBudget = {
        ...props.campaignBudget,
        intervalUnit: value
      }
      props.onBudgetChange(newBudget)
    }
  }

  const isCampaignEnded = useMemo(() => {
    return props.campaign.status === CampaignStatus.CampaignEnded || props.campaign.status === CampaignStatus.Deleted || props.campaign.status === CampaignStatus.Archived
  }, [props.campaign.status])

  const isCampaignActive = useMemo(() => {
    return props.campaign.activeStatus === CampaignActiveStatus.Active
  }, [props.campaign.activeStatus])

  const isScheduleLocked = useMemo(() => {
    return props.campaign.hasBundle || isCampaignActive || isCampaignEnded
  }, [props.campaign.hasBundle, isCampaignActive, isCampaignEnded])

  const isBudgetFieldDisabled = useMemo(() => {
    const hasStartedInPast = !!props.campaign.activated && (props.campaignBudget.start ?? moment()) < moment()
    return hasStartedInPast || props.campaign.hasBundle || isCampaignEnded
  }, [props.campaign.activated, props.campaign.hasBundle, props.campaignBudget.start, isCampaignEnded])

  const intervalUnitOptions = [
    { value: IntervalUnit.Week, text: 'Weekly' },
    { value: IntervalUnit.Month, text: 'Monthly' }
  ]

  const getDurationUnitForIntervalUnit = () => {
    if (props.campaignBudget.intervalUnit === IntervalUnit.Month) {
      return 'Months'
    }
    return 'Weeks'
  }

  const getBudgetHelpText = () => {
    if (props.campaignBudget.method === BudgetMethod.Dates) {
      return 'Your total budget will be evenly spread across the life of your campaign.'
    }

    return undefined
  }

  const isToManyDecimals = useMemo(() => {
    return Math.floor(props.campaignBudget.budget) !== props.campaignBudget.budget && props.campaignBudget.budget.toString().split('.')[1].length > 2
  }, [props.campaignBudget.budget])

  const isBudgetValid = useMemo(() => {
    if (canBudgetBeEmpty && budgetInputValue === '') {
      return true
    }

    if (!canBudgetBeEmpty && props.campaignBudget.budget === props.campaign.budget) {
      return true
    }

    const isBelowMinimum = (canBudgetBeEmpty && budgetInputValue !== '' && props.campaignBudget.budget != null && props.campaignBudget.budget < minBudget) || (!canBudgetBeEmpty && props.campaignBudget.budget != null && props.campaignBudget.budget < minBudget)
    return !isBelowMinimum
  }, [canBudgetBeEmpty, budgetInputValue, props.campaignBudget.budget, props.campaign.budget, minBudget])

  let budgetErrorMessage = `Minimum budget is ${formatter.format(minBudget)}`

  if (props.campaignBudget.method === BudgetMethod.Dates && props.campaignBudget.start && props.campaignBudget.end) {
    const start = props.campaignBudget.start
    const end = props.campaignBudget.end
    const days = end.diff(start, 'days') + 1
    budgetErrorMessage += ` for ${days} days.`
  }

  if (isToManyDecimals) {
    budgetErrorMessage = 'Please enter a budget with no more than 2 decimal places.'
  }

  const getMinDateForEndDate = () => {
    if (props.campaignBudget.start) {
      return props.campaignBudget.start
    }

    if (props.campaign?.activated) {
      return moment(props.campaign.activated).add(3, 'day')
    }

    return moment()
  }

  return (
    <React.Fragment>
      {props.campaignBudget.method === BudgetMethod.Indefinite ? (
        <React.Fragment>
          <EuiFormRow
            label={
              <EuiText size='xs' color={'primary'}>
                <strong>Budget Frequency</strong>
              </EuiText>
            }>
            <EuiSelect options={intervalUnitOptions} value={props.campaignBudget.intervalUnit ?? IntervalUnit.Week} onChange={e => onIntervalUnitChange(e.target.value as IntervalUnit)} disabled={isScheduleLocked} />
          </EuiFormRow>
        </React.Fragment>
      ) : (
        <React.Fragment>
          <EuiFormRow
            label={
              <EuiText size='xs' color={'primary'}>
                <strong>Start Date</strong>
              </EuiText>
            }>
            <EuiDatePicker fullWidth maxDate={props.campaignBudget.end} disabled={isScheduleLocked} selected={props.campaignBudget.start} dateFormat='MM/DD/yyyy' onChange={onStartChange} minDate={minStartDate} showTimeSelect={false} />
          </EuiFormRow>
          {props.campaignBudget.method === BudgetMethod.Dates && (
            <EuiFormRow
              label={
                <EuiText size='xs' color={'primary'}>
                  <strong>End Date</strong>
                </EuiText>
              }>
              <EuiDatePicker utcOffset={0} fullWidth disabled={isScheduleLocked} selected={props.campaignBudget.end} dateFormat='MM/DD/yyyy' onChange={onEndChange} minDate={getMinDateForEndDate()} showTimeSelect={false} />
            </EuiFormRow>
          )}
          {props.campaignBudget.method === BudgetMethod.Intervals && (
            <React.Fragment>
              <EuiFormRow
                label={
                  <EuiText size='xs' color={'primary'}>
                    <strong>Duration</strong>
                  </EuiText>
                }>
                <EuiFieldNumber onChange={e => onDurationChange(Number(e.target.value))} disabled={isScheduleLocked} min={1} append={getDurationUnitForIntervalUnit()} value={props.campaignBudget.totalIntervals ?? 1} />
              </EuiFormRow>
              <EuiFormRow
                label={
                  <EuiText size='xs' color={'primary'}>
                    <strong>Budget Frequency</strong>
                  </EuiText>
                }>
                <EuiSelect options={intervalUnitOptions} value={props.campaignBudget.intervalUnit ?? IntervalUnit.Week} onChange={e => onIntervalUnitChange(e.target.value as IntervalUnit)} disabled={isScheduleLocked} />
              </EuiFormRow>
            </React.Fragment>
          )}
        </React.Fragment>
      )}
      <AcEuiFormRow
        label={
          <EuiText size='xs' color={'primary'}>
            <strong>{getBudgetLabel()}</strong>
          </EuiText>
        }
        helpText={getBudgetHelpText()}
        isInvalid={isToManyDecimals || !isBudgetValid}
        error={budgetErrorMessage}
        showErrorOnInitialLoad={!canBudgetBeEmpty || (canBudgetBeEmpty && budgetInputValue !== '')}>
        <EuiFieldNumber step={0.01} fullWidth prepend={'$'} value={canBudgetBeEmpty ? undefined : props.campaignBudget.budget} onChange={e => onBudgetChange(e.target.value)} min={minBudgetForField} disabled={isBudgetFieldDisabled} />
      </AcEuiFormRow>
      <EuiSpacer />
    </React.Fragment>
  )
}
